// utils/diseaseUtils.js
import {
  fetchDiseases,
  fetchDiseaseDetails,
  fetchSymptomsByDisease,
  fetchCropDetails
} from '../api/cropService';

/**
 * Load diseases and crop name by crop ID.
 */
export async function getDiseasesAndCropName(cid) {
  const diseases = await fetchDiseases(cid);
  const cropDetails = await fetchCropDetails(cid);
  return {
    diseases,
    cropName: cropDetails?.name || "Unknown Crop"
  };
}

/**
 * Fetch disease details and symptoms by disease ID.
 */
export async function getDiseaseFullDetails(issue, currentCropName = '') {
  const data = await fetchDiseaseDetails(issue.disId);
  try {
    const symptomsData = await fetchSymptomsByDisease(issue.disId);
    data.symptoms = symptomsData;
  } catch (e) {
    console.error('Failed to fetch symptoms:', e);
    data.symptoms = null;
  }

  const cropName = data.cropName && data.cropName !== currentCropName
    ? data.cropName
    : currentCropName;

  return {
    selectedDisease: issue,
    diseaseDetails: data,
    cropName
  };
}

/**
 * Fetch disease details and symptoms by just disease ID.
 */

export async function getDiseaseFullDetailsById(disId, currentCropName = '') {
  const data = await fetchDiseaseDetails(disId);
  try {
    const symptomsData = await fetchSymptomsByDisease(disId);
    data.symptoms = symptomsData;
  } catch (e) {
    console.error('Failed to fetch symptoms:', e);
    data.symptoms = null;
  }

  const cropName = data.cropName && data.cropName !== currentCropName
    ? data.cropName
    : currentCropName;

  return {
    diseaseDetails: data,
    cropName
  };
}


/**
 * Helper to safely render a symptom list
 */
// export function renderSymptoms(symptoms) {
//   if (!symptoms) return null;

//   if (Array.isArray(symptoms)) {
//     return symptoms.map((symptom, index) => {
//       const text = symptom?.name || symptom?.qn || symptom?.description || JSON.stringify(symptom);
//       return <p key={index} className="mb-2">&lt;{text}&gt;</p>;
//     });
//   }

//   if (typeof symptoms === 'object') {
//     const text = symptoms.name || symptoms.qn || symptoms.description || JSON.stringify(symptoms);
//     return <p>&lt;{text}&gt;</p>;
//   }

//   return <p>&lt;{symptoms}&gt;</p>;
// }
export function renderSymptoms(symptoms) {
  if (!symptoms) return null;

  // Convert symptoms into a uniform text array
  let symptomList = [];

  if (Array.isArray(symptoms)) {
    symptomList = symptoms.map(symptom =>
      symptom?.name || symptom?.qn || symptom?.description || JSON.stringify(symptom)
    );
  } else if (typeof symptoms === 'object') {
    symptomList = [
      symptoms.name || symptoms.qn || symptoms.description || JSON.stringify(symptoms)
    ];
  } else {
    symptomList = [String(symptoms)];
  }

  // Render as list
  return (
    <ul className="ml-6 list-disc text-lg space-y-2">
      {symptomList.map((text, index) => (
        <li key={index}>{text}</li>
      ))}
    </ul>
  );
}

/**
 * Helper to safely render advice list
 */
// export function renderAdviceList(advice) {
//   if (!advice) return null;

//   if (Array.isArray(advice)) {
//     return advice.map((item, index) => {
//       const text = typeof item === 'object' ? (item.name || item.description || JSON.stringify(item)) : item;
//       return <p key={index} className="mb-2">• {text}</p>;
//     });
//   }

//   const text = typeof advice === 'object' ? (advice.name || advice.description || JSON.stringify(advice)) : advice;
//   return <p>• {text}</p>;
// }

export function renderAdviceList(advice) {
  let text = advice;
  if (!text) return null;

  // Remove bullet prefix if exists
  text = text.replace(/^•\s*/, '').trim();

  // Split using "Note:" (case insensitive, ignoring spaces around it)
  const parts = text.split(/note\s*:\s*/i);
  const mainText = parts[0].trim();
  const noteText = parts[1] ? parts[1].trim() : null;

  // Extract numbered pattern like (1). ... (2). ...
  const itemMatches = mainText.match(/\(\d+\)\s*[^()]+/g);

  // If no numbered items found, show as paragraph
  if (!itemMatches) {
    return (
      <div className="ml-6">
        <p className="text-lg">• {mainText}</p>
        {noteText && (
          <p className="mt-4 text-base text-gray-700">
            <span className="font-semibold">Note:</span> {noteText}
          </p>
        )}
      </div>
    );
  }

  // Clean numbered items
  const items = itemMatches.map(item =>
    item.replace(/\(\d+\)\.?\s*/, '').trim()
  );

  return (
    <div className="ml-6">
      <ol className="list-decimal ml-4 text-lg space-y-2">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>

      {noteText && (
        <p className="mt-4 text-base text-gray-700">
          <span className="font-semibold">Note:</span> {noteText}
        </p>
      )}
    </div>
  );
};

/**
 * Helper to safely render important notes
 */
export function renderImportantNotes(notes) {
  if (!notes) return null;

  if (Array.isArray(notes)) {
    return notes.map((note, index) => {
      const text = typeof note === 'object' ? (note.name || note.description || JSON.stringify(note)) : note;
      return <p key={index} className="mb-1">{index + 1}. {text}</p>;
    });
  }

  const text = typeof notes === 'object' ? (notes.name || notes.description || JSON.stringify(notes)) : notes;
  return <p>{text}</p>;
}
